import { useCallback } from 'react'
import { useSelector } from 'react-redux'

import { useResetEditingState } from '@/atoms'
import {
  selectUserConfiguration,
  selectUserConfigurationWeekStartsOn,
  selectUserProfile,
} from '@/features/domain/user'
import { createUser, updateUser } from '@/features/domain/user/actions'
import { useIsUnmounted, useNotification } from '@/hooks'
import { useAppDispatch } from '@/store'

import { createCreateUserPayload } from './createCreateUserPayload'
import { createUpdateUserPayload } from './createUpdateUserPayload'

import type { UserFormValues } from '../../types'
import { useTexts } from '../useTexts'

export function useOnSubmit(creating: boolean) {
  const onDiscard = useResetEditingState()
  const dispatch = useAppDispatch()
  const isUnMounted = useIsUnmounted()
  const userConfiguration = useSelector(selectUserConfiguration)
  const weekStartsOn = useSelector(selectUserConfigurationWeekStartsOn)
  const userProfile = useSelector(selectUserProfile)
  const toast = useNotification()
  const texts = useTexts()

  const onSubmit = useCallback(
    async (formValues: UserFormValues) => {
      try {
        const request = creating
          ? await dispatch(
              createUser(
                createCreateUserPayload({
                  formValues,
                  userConfiguration,
                  weekStartsOn,
                }),
              ),
            )
          : await dispatch(
              updateUser(
                createUpdateUserPayload({
                  formValues,
                  userConfiguration,
                  weekStartsOn,
                  uiSettings:
                    userProfile.user.type === 'gpsonly' ? undefined : userProfile.user.uiData,
                }),
              ),
            )

        const action = creating ? createUser : updateUser

        if (action.rejected.match(request)) {
          throw new Error(request.payload?.message ?? 'Internal error')
        }

        onDiscard()

        if (isUnMounted()) return
      } catch (error) {
        toast.error(error.message ?? texts.errors.edit)
      }
    },
    [dispatch, creating, onDiscard, userConfiguration, weekStartsOn, toast, userProfile, texts],
  )

  return {
    onSubmit,
  }
}
